<template>
  <div>
    <vue-headful :title="title" />
    <div class="mx-4 my-2" v-if="terminal">
      <div class="card">
        <div class="card-header">
          <div class="card-header-title">
            <p>Showing usage for {{ selectedDate.getUTCMonth()+1 }}/{{ selectedDate.getUTCFullYear() }}</p>
          </div>
        </div>
        <div class="card-content" style="position: relative">
          <b-loading :is-full-page="false" v-model="usageForMonthLoading" :can-cancel="false"></b-loading>
          <div class="columns">
            <div class="column is-three-quarters">
              <b-message v-if="usageForMonth">
                Total usage for {{ selectedDate.getUTCMonth()+1 }}/{{ selectedDate.getUTCFullYear() }} is <strong>
                {{ 
                  $numeral((usageForMonth.satelliteMonthlyUsage || 0) + (usageForMonth.cellularMonthlyUsage || 0)).format('0.00 b')
                }}</strong> of which <strong>
                {{ 
                  usageForMonth.satelliteMonthlyUsage ? $numeral(usageForMonth.satelliteMonthlyUsage).format('0.00 b') : 0
                }}</strong> is Satellite and <strong>
                {{ 
                  usageForMonth.cellularMonthlyUsage ? $numeral(usageForMonth.cellularMonthlyUsage).format('0.00 b') : 0
                }}</strong> is cellular.
              </b-message>
              <table class="table is-fullwidth is-narrow">
                <thead>
                  <tr>
                    <td><strong>Date</strong></td>
                    <td><strong>Cellular Usage</strong></td>
                    <td><strong>Satellite Usage</strong></td>
                  </tr>
                </thead>
                <tbody v-if="usageForDays.length">
                  <tr v-for="d in usageForDays" :key="d.date">
                    <td>{{ d.date }}</td>
                    <td>{{ getUsageForDay(d.day, 'cellular') ? $numeral(getUsageForDay(d.day, 'cellular')).format('0.00 b'): '-' }}</td>
                    <td>{{ getUsageForDay(d.day, 'satellite') ? $numeral(getUsageForDay(d.day, 'satellite')).format('0.00 b'): '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="column is-one-quarter">
              <div class="pl-4 action-links-container">
                <b-field>
                  <b-datepicker 
                    expanded
                    v-model="selectedDate"
                    position="is-bottom-left"
                    type="month"
                    placeholder="Click to select..."
                    icon="calendar"
                    @input="dateChanged"
                    trap-focus>
                  </b-datepicker>
                </b-field>
                <hr />
                <p v-if="subscriptionForMonth"><router-link :to="{ name: 'subscription2Overview', params: { id: subscriptionForMonth.id }}">View subscription active during {{ selectedDate.getUTCMonth()+1 }}/{{ selectedDate.getUTCFullYear() }}</router-link></p>
                <p class="mt-2"><a @click="exportCsv">Export CSV</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted, ref } from 'vue';
import {
  getDaysInMonth, 
  format, 
  isBefore, 
  isAfter, 
  addMonths,
  subSeconds
} from 'date-fns';
import { useTerminalsStore } from '../terminals-store';
import exportCsvArray from '../../utilities/exportCsvArray';

const store = useTerminalsStore();
const terminal = computed(() => store.state.terminal);
const title = computed(() => `${terminal.value?.name} / Usage History`);
const usageForMonth = computed(() => store.state.usage);
const usageForMonthLoading = computed(() => store.state.usageLoading);
const selectedDate = ref(new Date());

// eslint-disable-next-line arrow-body-style
const subscriptionForMonth = computed(() => {
  return terminal?.value?.subscriptionHistory?.find(s => {
    const rangeStartDate = selectedDate.value;
    const rangeEndDate = subSeconds(addMonths(rangeStartDate, 1), 1);
    return isBefore(new Date(s.startDate), rangeStartDate)
    && (!s.endDate || isAfter(new Date(s.endDate, rangeEndDate)))
  });
});

const fetchTerminalDailyUsage = async () => {
  await store.dispatch('fetchTerminalUsage', { year: selectedDate.value.getFullYear(), month: selectedDate.value.getMonth() + 1 });
}

onMounted(async () => {
  await fetchTerminalDailyUsage();
})

const dateChanged = async () => {
  await fetchTerminalDailyUsage();
}

const usageForDays = computed(() => {
  let d = getDaysInMonth(selectedDate.value); // Get how many days there are in the month we selected
  const arrDays = []; // Instatiate the return array
  while (d) { // While the day count is not 0
    const current = new Date(selectedDate.value.getUTCFullYear(), selectedDate.value.getUTCMonth(), d); // Get the date for the year, month
    arrDays.push({ date: `${format(current, 'yyyy-MM-dd')}`, day: d }); // push to return array
    d--; // decrement
  }
  return arrDays.reverse();
});

const getUsageForDay = (day, type) => {
  if (type == 'satellite') return usageForMonth.value?.satelliteDailyUsage?.find(u => u.day == day)?.usageValue;
  return usageForMonth.value?.cellularDailyUsage?.find(u => u.day == day)?.usageValue;
}

const exportCsv = async () => {
  const headers = ['Date', 'CellularUsage', 'SatelliteUsage'];
  await exportCsvArray(headers, usageForDays.value.map(o => (Object.values({ a: o.date, b: getUsageForDay(o.day, 'cellular'), c: getUsageForDay(o.day, 'satellite') }))), `usage-${terminal.value.serial}-${format(selectedDate.value, 'yyyy-MM-dd')}`);
}
</script>
